/**
 * PhaserVexFlowContext
 *
 * Implements VexFlow's RenderContext interface using Phaser's Graphics API.
 * This mirrors VexFlow's SVGContext API to ensure full compatibility.
 *
 * VexFlow 1.2.93 (used by OSMD) expects the following properties and methods
 * to be available on the context object.
 */

import Phaser from 'phaser';
import { NoteContainer } from './NoteContainer';

/**
 * PhaserVexFlowContext implements the same interface as VexFlow's SVGContext.
 * VexFlow accesses properties like `attributes`, `state`, and `lineWidth` directly,
 * so we must expose them as public properties.
 */
export class PhaserVexFlowContext {
  private scene: Phaser.Scene;
  private graphics: Phaser.GameObjects.Graphics;
  private container: Phaser.GameObjects.Container;
  private textObjects: Phaser.GameObjects.Text[] = [];

  // ==================== Container Routing for Interactive Elements ====================

  /** Map of element IDs to their NoteContainer instances */
  private elementContainers: Map<string, NoteContainer> = new Map();

  /** Stack of graphics objects for nested container routing */
  private graphicsStack: Phaser.GameObjects.Graphics[] = [];

  /** Stack of containers for nested container routing */
  private containerStack: Phaser.GameObjects.Container[] = [];

  /** Currently active element container (when inside an interactive element group) */
  private activeElementContainer: NoteContainer | null = null;

  /** Counter for generating unique element IDs when VexFlow doesn't provide one */
  private elementCounter: number = 0;

  /** Flag to enable/disable container routing for interactive elements */
  private enableContainerRouting: boolean = false;

  /** Set of VexFlow group classes that should get their own containers */
  private readonly INTERACTIVE_ELEMENTS = new Set([
    'stavenote', // Entire note (head + stem + flag + ledgers + modifiers)
    'beam', // Beam groups connecting notes
    // Can add more: "rest", "chord", etc.
  ]);

  // Path state (matching SVGContext)
  public path: string = '';
  public pen: { x: number; y: number } = { x: NaN, y: NaN };
  public lineWidth: number = 1.0;

  // State object (matching SVGContext)
  public state: {
    scale: { x: number; y: number };
    'font-family': string;
    'font-size': string;
    'font-weight': string;
    'font-style': string;
  } = {
    scale: { x: 1, y: 1 },
    'font-family': 'Arial',
    'font-size': '8pt',
    'font-weight': 'normal',
    'font-style': 'normal',
  };

  // Attributes object (matching SVGContext)
  public attributes: {
    'stroke-width': number;
    fill: string;
    stroke: string;
    'stroke-dasharray': string;
    'font-family': string;
    'font-size': string;
    'font-weight': string;
    'font-style': string;
    'fill-opacity'?: number;
  } = {
    'stroke-width': 0.6, // Thin stroke for elegant lines (matching reference image)
    fill: 'black',
    stroke: 'black',
    'stroke-dasharray': 'none',
    'font-family': 'Arial',
    'font-size': '10pt',
    'font-weight': 'normal',
    'font-style': 'normal',
  };

  // Background attributes (matching SVGContext)
  public background_attributes: {
    'stroke-width': number;
    fill: string;
    stroke: string;
  } = {
    'stroke-width': 0,
    fill: 'white',
    stroke: 'white',
  };

  // Shadow attributes (matching SVGContext)
  public shadow_attributes: {
    width: number;
    color: string;
  } = {
    width: 0,
    color: 'black',
  };

  // State stack for save/restore
  private state_stack: Array<{
    state: typeof PhaserVexFlowContext.prototype.state;
    attributes: typeof PhaserVexFlowContext.prototype.attributes;
    shadow_attributes: typeof PhaserVexFlowContext.prototype.shadow_attributes;
    lineWidth: number;
  }> = [];

  // Group stack for SVG compatibility
  private groups: Array<{ cls?: string; id?: string }> = [];

  // Track if we're currently inside a clef or brace group (to skip VexFlow's glyph rendering)
  private insideClefGroup: boolean = false;
  private insideBraceGroup: boolean = false;

  // Flag to enable/disable clef/brace glyph skipping (set by scene when using custom elements)
  private skipClefRendering: boolean = false;

  // Dimensions
  public width: number = 0;
  public height: number = 0;
  private defaultMusicColor: string = 'black';

  // For IE polyfill compatibility
  public ie: boolean = false;
  public fontSize: number = 10;

  constructor(
    scene: Phaser.Scene,
    graphics: Phaser.GameObjects.Graphics,
    container: Phaser.GameObjects.Container,
  ) {
    this.scene = scene;
    this.graphics = graphics;
    this.container = container;
  }

  /**
   * Get the Phaser container for adding custom game objects.
   */
  getContainer(): Phaser.GameObjects.Container {
    return this.container;
  }

  /**
   * Get the Phaser scene.
   */
  getScene(): Phaser.Scene {
    return this.scene;
  }

  setDefaultMusicColor(color: string): this {
    this.defaultMusicColor = color || 'black';
    return this;
  }

  /**
   * Enable or disable skipping of VexFlow clef glyph rendering.
   * When enabled, clef glyphs won't be drawn (space is still reserved),
   * allowing custom clefs to be rendered instead.
   */
  setSkipClefRendering(skip: boolean): void {
    this.skipClefRendering = skip;
  }

  /**
   * Create an SVG element (no-op for Phaser, but needed for interface compatibility)
   */
  create(_svgElementType: string): Record<string, never> {
    // Return a mock object since Phaser doesn't create SVG elements
    return {};
  }

  /**
   * Open a group for organizing elements.
   * Tracks clef and brace groups to skip their glyph rendering when custom elements are enabled.
   * When container routing is enabled, creates dedicated NoteContainer instances for interactive elements.
   */
  openGroup(
    cls?: string,
    id?: string,
    _attrs?: { pointerBBox?: boolean },
  ): {
    setAttribute: () => void;
    getAttribute: () => null;
    classList: {
      add: () => void;
      remove: () => void;
      contains: () => boolean;
      toggle: () => boolean;
    };
    id: string;
    className: string;
    style: object;
    appendChild: () => void;
    removeChild: () => void;
    children: unknown[];
    childNodes: unknown[];
    parentNode: null;
    parentElement: null;
  } {
    this.groups.push({ cls, id });

    // Detect if this is a clef, brace, or connector group (to skip VexFlow's default rendering)
    if (this.skipClefRendering && cls) {
      if (cls.includes('clef')) {
        this.insideClefGroup = true;
      }
      // Skip brace and connector groups - we render our own braces
      if (cls.includes('brace') || cls.includes('bracket') || cls.includes('connector')) {
        this.insideBraceGroup = true;
      }
    }

    // Container routing: create dedicated container for interactive elements
    if (this.enableContainerRouting && cls && this.INTERACTIVE_ELEMENTS.has(cls)) {
      // Generate a unique ID if VexFlow didn't provide one
      const elementId = id || `${cls}-${++this.elementCounter}`;

      // Create a new NoteContainer for this element
      const noteContainer = new NoteContainer({
        scene: this.scene,
        id: elementId,
        elementType: cls,
      });

      // Push current graphics and container onto stacks
      this.graphicsStack.push(this.graphics);
      this.containerStack.push(this.container);

      // Switch to the new NoteContainer's graphics
      this.graphics = noteContainer.getGraphics();

      // Add the NoteContainer to the parent container
      this.container.add(noteContainer.getContainer());

      // Store reference for later retrieval
      this.elementContainers.set(elementId, noteContainer);
      this.activeElementContainer = noteContainer;
    }

    // Return a mock SVG group object that mimics SVGGElement
    // OSMD code accesses classList.add() and setAttribute() on this object
    return {
      setAttribute: () => {},
      getAttribute: () => null,
      classList: {
        add: () => {},
        remove: () => {},
        contains: () => false,
        toggle: () => false,
      },
      id: id || '',
      className: cls || '',
      style: {},
      appendChild: () => {},
      removeChild: () => {},
      children: [],
      childNodes: [],
      parentNode: null,
      parentElement: null,
    };
  }

  /**
   * Close the current group.
   * Resets clef/brace group tracking when exiting those groups.
   * When container routing is enabled, restores the previous graphics context.
   */
  closeGroup(): void {
    const closingGroup = this.groups.pop();

    // Reset group flags when exiting
    if (closingGroup?.cls) {
      if (closingGroup.cls.includes('clef')) {
        this.insideClefGroup = false;
      }
      if (
        closingGroup.cls.includes('brace') ||
        closingGroup.cls.includes('bracket') ||
        closingGroup.cls.includes('connector')
      ) {
        this.insideBraceGroup = false;
      }

      // Container routing: restore previous graphics context when exiting interactive element
      if (this.enableContainerRouting && this.INTERACTIVE_ELEMENTS.has(closingGroup.cls)) {
        // Update bounds on the element we just finished drawing
        if (this.activeElementContainer) {
          this.activeElementContainer.updateBounds();
        }

        // Restore previous graphics and container from stacks
        const previousGraphics = this.graphicsStack.pop();
        const previousContainer = this.containerStack.pop();

        if (previousGraphics) {
          this.graphics = previousGraphics;
        }
        if (previousContainer) {
          this.container = previousContainer;
        }

        this.activeElementContainer = null;
      }
    }
  }

  /**
   * Add an element to the current group (no-op for Phaser)
   */
  add(_elem: unknown): void {
    // No-op for Phaser
  }

  // ==================== Styling & State Methods ====================

  setFont(family: string, size: number | string, weight?: string): this {
    let bold = false;
    let italic = false;
    let style = 'normal';

    if (typeof weight === 'string') {
      if (weight.indexOf('italic') !== -1) {
        weight = weight.replace(/italic/g, '');
        italic = true;
      }
      if (weight.indexOf('bold') !== -1) {
        weight = weight.replace(/bold/g, '');
        bold = true;
      }
      weight = weight.replace(/ /g, '');
    }

    weight = bold ? 'bold' : weight;
    weight = typeof weight === 'undefined' || weight === '' ? 'normal' : weight;
    style = italic ? 'italic' : style;

    const sizeStr = typeof size === 'number' ? `${size}pt` : size;
    this.fontSize = typeof size === 'number' ? size : parseInt(size, 10);

    const fontAttributes = {
      'font-family': family,
      'font-size': sizeStr,
      'font-weight': weight as string,
      'font-style': style,
    };

    Object.assign(this.attributes, fontAttributes);
    Object.assign(this.state, fontAttributes);

    return this;
  }

  setRawFont(font: string): this {
    font = font.trim();
    const fontArray = font.split(' ');

    this.attributes['font-family'] = fontArray[1];
    this.state['font-family'] = fontArray[1];
    this.attributes['font-size'] = fontArray[0];
    this.state['font-size'] = fontArray[0];
    this.fontSize = parseInt(fontArray[0].match(/\d+/)?.[0] || '10', 10);

    return this;
  }

  setFillStyle(style: string): this {
    this.attributes.fill = style;
    return this;
  }

  setBackgroundFillStyle(style: string): this {
    this.background_attributes.fill = style;
    this.background_attributes.stroke = style;
    return this;
  }

  setStrokeStyle(style: string): this {
    this.attributes.stroke = style;
    return this;
  }

  setShadowColor(style: string): this {
    this.shadow_attributes.color = style;
    return this;
  }

  setShadowBlur(blur: number): this {
    this.shadow_attributes.width = blur;
    return this;
  }

  setLineWidth(width: number): this {
    this.attributes['stroke-width'] = width;
    this.lineWidth = width;
    return this;
  }

  setLineDash(lineDash: number[]): this {
    if (Array.isArray(lineDash)) {
      this.attributes['stroke-dasharray'] = lineDash.join(', ');
    }
    return this;
  }

  setLineCap(_lineCap: string): this {
    // Phaser doesn't support line cap directly
    return this;
  }

  // ==================== Sizing & Scaling Methods ====================

  resize(width: number, height: number): this {
    this.width = width;
    this.height = height;
    this.scale(this.state.scale.x, this.state.scale.y);
    return this;
  }

  scale(x: number, y: number): this {
    // Store the scale state for reference
    this.state.scale = { x, y };
    // Note: We do NOT apply scale to the Phaser container here.
    // VexFlow/OSMD already passes coordinates in the scaled coordinate space.
    return this;
  }

  setViewBox(_xMin: number, _yMin: number, _width: number, _height: number): void {
    // No-op for Phaser (SVG-specific)
  }

  // ==================== Container Routing Public Methods ====================

  /**
   * Enable or disable container routing for interactive elements.
   * When enabled, VexFlow elements like notes and beams get their own
   * Phaser Containers, allowing individual animation and interaction.
   */
  setContainerRouting(enabled: boolean): void {
    this.enableContainerRouting = enabled;
  }

  /**
   * Check if container routing is enabled.
   */
  isContainerRoutingEnabled(): boolean {
    return this.enableContainerRouting;
  }

  /**
   * Get a specific element container by its ID.
   */
  getElementContainer(id: string): NoteContainer | undefined {
    return this.elementContainers.get(id);
  }

  /**
   * Get all element containers of a specific type.
   */
  getElementContainersByType(elementType: string): NoteContainer[] {
    const containers: NoteContainer[] = [];
    for (const container of this.elementContainers.values()) {
      if (container.elementType === elementType) {
        containers.push(container);
      }
    }
    return containers;
  }

  /**
   * Get all element containers.
   */
  getAllElementContainers(): NoteContainer[] {
    return Array.from(this.elementContainers.values());
  }

  /**
   * Get all element container IDs.
   */
  getElementContainerIds(): string[] {
    return Array.from(this.elementContainers.keys());
  }

  /**
   * Clear all element containers and reset routing state.
   */
  clearElementContainers(): void {
    // Destroy all containers
    for (const container of this.elementContainers.values()) {
      container.destroy();
    }
    this.elementContainers.clear();

    // Reset stacks
    this.graphicsStack = [];
    this.containerStack = [];

    // Reset counter and active container
    this.elementCounter = 0;
    this.activeElementContainer = null;
  }

  // ==================== Shape & Path Methods ====================

  clear(): void {
    this.graphics.clear();

    // Clear text objects
    this.textObjects.forEach((textObj) => textObj.destroy());
    this.textObjects = [];

    // Clear element containers (from container routing)
    this.clearElementContainers();

    // Reset path
    this.path = '';
    this.pen = { x: NaN, y: NaN };
  }

  rect(
    x: number,
    y: number,
    width: number,
    height: number,
    _attributes?: Record<string, unknown>,
  ): this {
    // Handle negative height
    if (height < 0) {
      y += height;
      height *= -1;
    }

    const strokeColor = this.parseColorHex(this.attributes.stroke);
    this.graphics.lineStyle(this.lineWidth, strokeColor, 1);
    this.graphics.strokeRect(x, y, width, height);
    return this;
  }

  fillRect(x: number, y: number, width: number, height: number): this {
    if (height < 0) {
      y += height;
      height *= -1;
    }

    const fillColor = this.parseColorHex(this.attributes.fill);
    const alpha = this.attributes['fill-opacity'] ?? 1;
    this.graphics.fillStyle(fillColor, alpha);
    this.graphics.fillRect(x, y, width, height);
    return this;
  }

  clearRect(x: number, y: number, width: number, height: number): this {
    const bgColor = this.parseColorHex(this.background_attributes.fill);
    this.graphics.fillStyle(bgColor, 1);
    this.graphics.fillRect(x, y, width, height);
    return this;
  }

  beginPath(): this {
    this.path = '';
    this.pen.x = NaN;
    this.pen.y = NaN;
    this.graphics.beginPath();
    return this;
  }

  moveTo(x: number, y: number): this {
    this.path += `M${x} ${y}`;
    this.pen.x = x;
    this.pen.y = y;
    this.graphics.moveTo(x, y);
    return this;
  }

  lineTo(x: number, y: number): this {
    this.path += `L${x} ${y}`;
    this.pen.x = x;
    this.pen.y = y;
    this.graphics.lineTo(x, y);
    return this;
  }

  bezierCurveTo(x1: number, y1: number, x2: number, y2: number, x: number, y: number): this {
    this.path += `C${x1} ${y1},${x2} ${y2},${x} ${y}`;

    // Phaser Graphics doesn't have native bezier, so we approximate
    this.approximateBezier(this.pen.x, this.pen.y, x1, y1, x2, y2, x, y);

    this.pen.x = x;
    this.pen.y = y;
    return this;
  }

  private approximateBezier(
    startX: number,
    startY: number,
    cp1x: number,
    cp1y: number,
    cp2x: number,
    cp2y: number,
    endX: number,
    endY: number,
    segments: number = 20,
  ): void {
    for (let i = 1; i <= segments; i++) {
      const t = i / segments;
      const t2 = t * t;
      const t3 = t2 * t;
      const mt = 1 - t;
      const mt2 = mt * mt;
      const mt3 = mt2 * mt;

      const px = mt3 * startX + 3 * mt2 * t * cp1x + 3 * mt * t2 * cp2x + t3 * endX;
      const py = mt3 * startY + 3 * mt2 * t * cp1y + 3 * mt * t2 * cp2y + t3 * endY;

      this.graphics.lineTo(px, py);
    }
  }

  quadraticCurveTo(x1: number, y1: number, x: number, y: number): this {
    this.path += `Q${x1} ${y1},${x} ${y}`;

    // Convert quadratic to cubic bezier
    const cp1x = this.pen.x + (2 / 3) * (x1 - this.pen.x);
    const cp1y = this.pen.y + (2 / 3) * (y1 - this.pen.y);
    const cp2x = x + (2 / 3) * (x1 - x);
    const cp2y = y + (2 / 3) * (y1 - y);

    this.approximateBezier(this.pen.x, this.pen.y, cp1x, cp1y, cp2x, cp2y, x, y);

    this.pen.x = x;
    this.pen.y = y;
    return this;
  }

  arc(
    x: number,
    y: number,
    radius: number,
    startAngle: number,
    endAngle: number,
    antiClockwise?: boolean,
  ): this {
    // Normalize angles
    const normalizeAngle = (angle: number): number => {
      while (angle < 0) angle += Math.PI * 2;
      while (angle > Math.PI * 2) angle -= Math.PI * 2;
      return angle;
    };

    startAngle = normalizeAngle(startAngle);
    endAngle = normalizeAngle(endAngle);

    if (startAngle > endAngle) {
      const tmp = startAngle;
      startAngle = endAngle;
      endAngle = tmp;
      antiClockwise = !antiClockwise;
    }

    const startDeg = Phaser.Math.RadToDeg(startAngle);
    const endDeg = Phaser.Math.RadToDeg(endAngle);
    this.graphics.arc(x, y, radius, startDeg, endDeg, antiClockwise ?? false);

    // Store arc in path for NoteContainer color changes (used for augmentation dots)
    // Approximate circle as bezier curves for path storage
    const angleDiff = Math.abs(endAngle - startAngle);
    if (angleDiff >= Math.PI * 1.9) {
      // Full circle - approximate with 4 quarter-circle beziers
      const kappa = 0.5522847498; // 4/3 * (sqrt(2) - 1) for bezier circle approximation
      const k = radius * kappa;

      // Start at right (0 degrees)
      this.path += `M${x + radius} ${y}`;
      // Quarter 1: right to bottom
      this.path += `C${x + radius} ${y + k} ${x + k} ${y + radius} ${x} ${y + radius}`;
      // Quarter 2: bottom to left
      this.path += `C${x - k} ${y + radius} ${x - radius} ${y + k} ${x - radius} ${y}`;
      // Quarter 3: left to top
      this.path += `C${x - radius} ${y - k} ${x - k} ${y - radius} ${x} ${y - radius}`;
      // Quarter 4: top to right
      this.path += `C${x + k} ${y - radius} ${x + radius} ${y - k} ${x + radius} ${y}`;
      this.path += 'Z';
    }

    return this;
  }

  closePath(): this {
    this.path += 'Z';
    this.graphics.closePath();
    return this;
  }

  glow(): this {
    // Shadow/glow effect - could be implemented with shader if needed
    // For now, no-op
    return this;
  }

  fill(_attributes?: Record<string, unknown>): this {
    // Skip VexFlow's clef and brace glyph rendering when custom elements are enabled
    // The layout space is still reserved by OSMD, but we don't draw the glyphs
    if (this.insideClefGroup || this.insideBraceGroup) {
      // Clear the path without drawing
      this.path = '';
      return this;
    }

    this.glow();

    const fillColor = this.parseColorHex(this.attributes.fill);
    const alpha = this.attributes['fill-opacity'] ?? 1;

    // Store fill path on the active NoteContainer for color changes
    // Fills are note heads, strokes are stems - only heads need color changes
    if (this.activeElementContainer && this.path.length > 0) {
      this.activeElementContainer.addFillPath(this.path);
    }

    // Check if path has multiple subpaths (needs evenodd for holes like whole notes)
    // Multiple M commands = multiple subpaths = potential hole
    const moveCommands = (this.path.match(/M/gi) || []).length;

    if (moveCommands >= 2) {
      // Use evenodd fill for compound paths (whole notes, half notes with holes)
      this.fillWithEvenOdd(fillColor, alpha);
    } else {
      // Use native Phaser fill for simple paths (quarter notes, etc.) - crisp rendering
      this.graphics.fillStyle(fillColor, alpha);
      this.graphics.fillPath();
    }

    return this;
  }

  /**
   * Fill the current path using the 'evenodd' fill rule.
   * This is necessary for compound paths like whole notes that have inner holes.
   */
  private fillWithEvenOdd(fillColor: number, alpha: number): void {
    // Create a temporary canvas to render the path with evenodd
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      // Fallback to regular fill
      this.graphics.fillStyle(fillColor, alpha);
      this.graphics.fillPath();
      return;
    }

    // Use device pixel ratio for crisp rendering on high-DPI displays
    const dpr = window.devicePixelRatio || 1;

    // Parse the SVG-style path to get bounds for canvas sizing
    const pathBounds = this.getPathBounds();
    const padding = 2;
    const logicalWidth = pathBounds.width + padding * 2;
    const logicalHeight = pathBounds.height + padding * 2;

    // Set canvas size accounting for device pixel ratio
    canvas.width = Math.ceil(logicalWidth * dpr);
    canvas.height = Math.ceil(logicalHeight * dpr);
    canvas.style.width = `${logicalWidth}px`;
    canvas.style.height = `${logicalHeight}px`;

    // Scale context for high-DPI rendering
    ctx.scale(dpr, dpr);

    // Set fill color (convert hex to CSS color)
    const r = (fillColor >> 16) & 0xff;
    const g = (fillColor >> 8) & 0xff;
    const b = fillColor & 0xff;
    ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`;

    // Replay the path commands on the canvas context
    ctx.beginPath();
    this.replayPathToContext(ctx, -pathBounds.x + padding, -pathBounds.y + padding);
    ctx.fill('evenodd');

    // Create a Phaser image from the canvas and add it to the container
    const textureKey = `evenodd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.scene.textures.addCanvas(textureKey, canvas);
    const image = this.scene.add.image(pathBounds.x - padding, pathBounds.y - padding, textureKey);
    image.setOrigin(0, 0);
    // Scale down the image to account for the high-DPI canvas
    image.setScale(1 / dpr);

    // If we're routing to a note container, add to that; otherwise add to main container
    const targetContainer = this.activeElementContainer?.getContainer() || this.container;
    targetContainer.add(image);

    // Clear the graphics path since we handled it separately
    this.graphics.beginPath();
  }

  /**
   * Get the bounding box of the current path.
   */
  private getPathBounds(): { x: number; y: number; width: number; height: number } {
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    const pathRegex = /([MLCQZ])([^MLCQZ]*)/gi;
    let match;
    let currentX = 0,
      currentY = 0;

    while ((match = pathRegex.exec(this.path)) !== null) {
      const cmd = match[1].toUpperCase();
      const args = match[2]
        .trim()
        .split(/[\s,]+/)
        .filter((s) => s)
        .map(parseFloat);

      switch (cmd) {
        case 'M':
        case 'L':
          currentX = args[0];
          currentY = args[1];
          minX = Math.min(minX, currentX);
          minY = Math.min(minY, currentY);
          maxX = Math.max(maxX, currentX);
          maxY = Math.max(maxY, currentY);
          break;
        case 'C':
          // Cubic bezier: control points and end point
          for (let i = 0; i < args.length; i += 2) {
            minX = Math.min(minX, args[i]);
            minY = Math.min(minY, args[i + 1]);
            maxX = Math.max(maxX, args[i]);
            maxY = Math.max(maxY, args[i + 1]);
          }
          currentX = args[4];
          currentY = args[5];
          break;
        case 'Q':
          // Quadratic bezier
          for (let i = 0; i < args.length; i += 2) {
            minX = Math.min(minX, args[i]);
            minY = Math.min(minY, args[i + 1]);
            maxX = Math.max(maxX, args[i]);
            maxY = Math.max(maxY, args[i + 1]);
          }
          currentX = args[2];
          currentY = args[3];
          break;
      }
    }

    if (!isFinite(minX)) {
      return { x: 0, y: 0, width: 100, height: 100 };
    }

    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
    };
  }

  /**
   * Replay the stored path commands to a canvas 2D context.
   */
  private replayPathToContext(
    ctx: CanvasRenderingContext2D,
    offsetX: number,
    offsetY: number,
  ): void {
    const pathRegex = /([MLCQZ])([^MLCQZ]*)/gi;
    let match;

    while ((match = pathRegex.exec(this.path)) !== null) {
      const cmd = match[1].toUpperCase();
      const args = match[2]
        .trim()
        .split(/[\s,]+/)
        .filter((s) => s)
        .map(parseFloat);

      switch (cmd) {
        case 'M':
          ctx.moveTo(args[0] + offsetX, args[1] + offsetY);
          break;
        case 'L':
          ctx.lineTo(args[0] + offsetX, args[1] + offsetY);
          break;
        case 'C':
          ctx.bezierCurveTo(
            args[0] + offsetX,
            args[1] + offsetY,
            args[2] + offsetX,
            args[3] + offsetY,
            args[4] + offsetX,
            args[5] + offsetY,
          );
          break;
        case 'Q':
          ctx.quadraticCurveTo(
            args[0] + offsetX,
            args[1] + offsetY,
            args[2] + offsetX,
            args[3] + offsetY,
          );
          break;
        case 'Z':
          ctx.closePath();
          break;
      }
    }
  }

  stroke(): this {
    // Skip VexFlow's clef and brace stroke rendering when custom elements are enabled
    if (this.insideClefGroup || this.insideBraceGroup) {
      // Clear the path without drawing
      this.path = '';
      return this;
    }

    this.glow();

    // Store stroke path on the active NoteContainer for color changes
    // Strokes are stems - needed for full note coloring
    if (this.activeElementContainer && this.path.length > 0) {
      this.activeElementContainer.addStrokePath(this.path, this.lineWidth);
    }

    const strokeColor = this.parseColorHex(this.attributes.stroke);
    this.graphics.lineStyle(this.lineWidth, strokeColor, 1);
    this.graphics.strokePath();

    return this;
  }

  // ==================== Text Methods ====================

  measureText(text: string): { x: number; y: number; width: number; height: number } {
    if (!this.scene || !this.scene.add) {
      // Return a default measurement if scene is unavailable
      return { x: 0, y: 0, width: text.length * 8, height: 12 };
    }
    // Create temporary text to measure
    const tempText = this.scene.add.text(0, 0, text, {
      fontFamily: this.state['font-family'],
      fontSize: this.state['font-size'],
      fontStyle: this.state['font-style'],
    });

    const bounds = tempText.getBounds();
    const result = {
      x: 0,
      y: 0,
      width: bounds.width,
      height: bounds.height,
    };

    tempText.destroy();
    return result;
  }

  fillText(text: string, x: number, y: number): void {
    if (!text || text.length === 0) return;
    if (!this.scene || !this.scene.add) return;

    const fillColor = this.attributes.fill;
    const fontStyle = this.state['font-style'] === 'italic' ? 'italic' : '';
    const fontWeight = this.state['font-weight'] === 'bold' ? 'bold' : '';
    const combinedStyle = `${fontStyle} ${fontWeight}`.trim() || undefined;

    const textObj = this.scene.add.text(x, y, text, {
      fontFamily: this.state['font-family'],
      fontSize: this.state['font-size'],
      fontStyle: combinedStyle,
      color: fillColor,
    });

    // VexFlow uses baseline alignment
    textObj.setOrigin(0, 1);
    this.container.add(textObj);
    this.textObjects.push(textObj);
  }

  // ==================== State Methods ====================

  save(): this {
    this.state_stack.push({
      state: { ...this.state, scale: { ...this.state.scale } },
      attributes: { ...this.attributes },
      shadow_attributes: { ...this.shadow_attributes },
      lineWidth: this.lineWidth,
    });
    return this;
  }

  restore(): this {
    const saved = this.state_stack.pop();
    if (saved) {
      this.state = { ...saved.state, scale: { ...saved.state.scale } };
      this.attributes = { ...saved.attributes };
      this.shadow_attributes = { ...saved.shadow_attributes };
      this.lineWidth = saved.lineWidth;
    }
    return this;
  }

  // ==================== Helper Methods ====================

  private shouldUseDefaultMusicColor(colorStr: string): boolean {
    const normalized = colorStr.trim().replace(/\s/g, '').toLowerCase();
    if (normalized === 'black') return true;
    if (/^rgba?\(0,0,0(?:,1(?:\.0)?)?\)$/.test(normalized)) return true;

    const hex = normalized.startsWith('#') ? normalized.slice(1) : normalized;
    if (hex.length === 3) {
      return hex === '000';
    }

    return hex.length >= 6 && hex.slice(0, 6) === '000000';
  }

  private parseColorHex(colorStr: string): number {
    if (!colorStr || colorStr === 'none') {
      return 0x000000;
    }

    let hex = this.shouldUseDefaultMusicColor(colorStr) ? this.defaultMusicColor : colorStr;
    if (hex.startsWith('#')) {
      hex = hex.slice(1);
    } else if (hex === 'black') {
      return 0x000000;
    } else if (hex === 'white') {
      return 0xffffff;
    } else if (hex === 'red') {
      return 0xff0000;
    } else if (hex === 'green') {
      return 0x00ff00;
    } else if (hex === 'blue') {
      return 0x0000ff;
    }

    // Handle 3-character hex
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((c) => c + c)
        .join('');
    }

    // Take only RGB portion
    if (hex.length >= 6) {
      hex = hex.slice(0, 6);
    }

    return parseInt(hex, 16) || 0x000000;
  }

  /**
   * Get the underlying Phaser Graphics object
   */
  getGraphics(): Phaser.GameObjects.Graphics {
    return this.graphics;
  }

  /**
   * Get bounds of the rendered content.
   * VexFlow may call this internally - returns the container bounds.
   */
  getBounds(): { x: number; y: number; w: number; h: number } {
    const bounds = this.container.getBounds();
    return {
      x: bounds.x,
      y: bounds.y,
      w: bounds.width,
      h: bounds.height,
    };
  }
}
